
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { ChevronUp, ChevronDown, ArrowUp, ArrowDown, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

interface DayTitleManagerProps {
  dayIndex: number;
  dayTitle: string;
  onUpdateDayTitle: (dayIndex: number, title: string) => void;
  onRemoveDay: (dayIndex: number) => void;
  onMoveDay: (dayIndex: number, direction: 'up' | 'down') => void;
  isOpen: boolean;
  totalDays: number;
}

export function DayTitleManager({
  dayIndex,
  dayTitle,
  onUpdateDayTitle,
  onRemoveDay,
  onMoveDay,
  isOpen,
  totalDays,
}: DayTitleManagerProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getDayNumber = () => {
    // Regex agora é multilíngue - aceita Dia/Day/Día/Jour
    const match = dayTitle.match(/^(Dia|Day|Día|Jour)\s+(\d+)/i);
    return match ? match[2] : (dayIndex + 1).toString();
  };

  const getTitleDescription = (title: string) => {
    // Regex agora é multilíngue - aceita Dia/Day/Día/Jour
    const match = title.match(/^(Dia|Day|Día|Jour)\s+\d+[\s:-]*(.+)/i);
    return match ? match[2].trim() : "";
  };

  const [descriptionInput, setDescriptionInput] = useState(getTitleDescription(dayTitle));

  useEffect(() => {
    const newDescriptionFromProp = getTitleDescription(dayTitle);
    console.log("useEffect [dayTitle]:", 
      {
        dayTitleFromProp: dayTitle,
        derivedDescription: newDescriptionFromProp, 
        currentDescriptionInput: descriptionInput, 
        areDifferent: newDescriptionFromProp !== descriptionInput 
      }
    );
    if (newDescriptionFromProp !== descriptionInput) {
      console.log("   >>> useEffect IS UPDATING descriptionInput FROM: ", descriptionInput, " TO: ", newDescriptionFromProp);
      setDescriptionInput(newDescriptionFromProp);
    }
  }, [dayTitle]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDescription = e.target.value;
    setDescriptionInput(newDescription);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const dayNum = getDayNumber();
      const prefix = t("newWorkout.days.title");
      const newFullTitle = newDescription
        ? `${prefix} ${dayNum} - ${newDescription}`
        : `${prefix} ${dayNum}`;
      onUpdateDayTitle(dayIndex, newFullTitle);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
      <div className="flex items-center gap-2 flex-1">
        <CollapsibleTrigger className="flex items-center gap-2">
          <h3 className="text-xl font-bold text-primary dark:text-primary mr-1">
            {t("newWorkout.days.title")} {dayIndex + 1}
          </h3>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CollapsibleTrigger>
        <Input 
          value={descriptionInput}
          onChange={handleTitleChange} 
          placeholder={t("newWorkout.days.titlePlaceholder")} 
          className={`${isMobile ? 'w-full' : 'max-w-[300px]'} h-8 bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600`}
        />
      </div>
      <div className="flex gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => onMoveDay(dayIndex, 'up')} disabled={dayIndex === 0} className="h-8 w-8 p-0" title={t("newWorkout.days.moveUp")}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => onMoveDay(dayIndex, 'down')} disabled={dayIndex === totalDays - 1} className="h-8 w-8 p-0" title={t("newWorkout.days.moveDown")}>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          onClick={() => onRemoveDay(dayIndex)} 
          disabled={totalDays === 1} 
          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
          title={t("newWorkout.days.removeDay")}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
